import React from 'react';
import { LocateFixed, MapPin } from 'lucide-react';
import { useDeliveryLocation } from '../context/DeliveryLocationContext';
import { btnPrimary, btnSecondary, btnSm, focusRing } from '../styles/ui';

// What to say when there is no browsing location, by why automatic detection
// did not produce one. The browser owns the permission prompt; this only
// explains, offers to ask again (from the customer's own tap) and always
// offers the manual location dialog.
const COPY = {
  denied: {
    title: "We couldn't access your location",
    body: 'Select your location or enter your pincode. To detect it automatically, allow location for this site in your browser settings.',
    retry: false,
  },
  timeout: {
    title: "We couldn't find your location",
    body: 'Finding your location took too long. Try again, or select your location manually.',
    retry: true,
  },
  unconfirmed: {
    title: 'Confirm your delivery area',
    body: "We found your position but couldn't confirm the area. Select your location or enter your pincode to see what we deliver.",
    retry: false,
  },
  unavailable: {
    title: "We couldn't find your location",
    body: 'Your current location is not available right now. Try again, or select your location manually.',
    retry: true,
  },
};
const DEFAULT_COPY = {
  title: 'Select Location to Check Availability',
  body: 'Stock and prices depend on where we deliver.',
  retry: false,
};

const LocationDetectionFallback = ({ className = '', testId = 'storefront-gate', dataState = 'unknown' }) => {
  const { locationDetection, detectCurrentLocation, openLocationPrompt } = useDeliveryLocation();
  const reason = locationDetection?.status === 'failed' ? locationDetection.reason : null;
  const copy = COPY[reason] || DEFAULT_COPY;

  return (
    <div
      data-testid={testId}
      data-state={dataState}
      data-detection={reason || undefined}
      className={`rounded-2xl border px-5 py-5 sm:px-6 border-emerald-100 bg-white shadow-soft ${className}`}
    >
      <div className="flex items-start gap-3">
      <span className="w-10 h-10 rounded-full bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0">
        <MapPin size={19} aria-hidden="true" />
      </span>
      <p className="min-w-0 flex-1 text-sm text-ink-secondary">
        <span className="block font-bold text-emerald-950">{copy.title}</span>
        {copy.body}
      </p>
      </div>
      <span className="flex flex-wrap gap-2 mt-3 sm:pl-[52px]">
        {copy.retry && detectCurrentLocation && (
          <button
            type="button"
            onClick={() => detectCurrentLocation()}
            className={`${btnSecondary} ${btnSm} ${focusRing}`}
          >
            <LocateFixed size={15} aria-hidden="true" />
            Use My Current Location
          </button>
        )}
        <button type="button" onClick={openLocationPrompt} className={`${btnPrimary} ${btnSm} ${focusRing}`}>
          Select location
        </button>
      </span>
    </div>
  );
};

export default LocationDetectionFallback;
